import { FC, useState } from 'react'
import AsyncSelect from 'react-select/async'
import { ActionMeta, components, MenuProps, MultiValueRemoveProps } from 'react-select'
import { XIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import styled from '@emotion/styled'
import { useDebounceFn } from 'ahooks'
import { useAppTheme } from '../../../theme/useAppTheme'
import { apiClient } from '../../../api/ApiClient'
import { ApiCatalogSearchItem, ApiCatalogToken } from '../../../api/catalog/types'
import { ApiUnitOfMeasureType } from '../../../api/chats/types/ApiChatPreview'
import { ResetButton } from '../../../common/components/ResetButton'

type ChipOption = { label: string; value: string }
type DropdownOption = ChipOption & { tokens: ApiCatalogToken[]; unitsOfMeasure: ApiUnitOfMeasureType[] }

interface PreviewAutocompleteProps {
  autoFocus?: boolean
  defaultTokens?: ApiCatalogToken[]
  onCancel?: () => void
  onSelect?: (selection: ApiCatalogSearchItem) => void
  originalRequest?: string
  searchRequest?: string
}

const Container = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  border: `1px solid ${theme.colors.primary.main}`,
  width: 'fit-content',
  minWidth: 'min(600px, 100%)',
  maxWidth: '100%',
  '&:hover': {
    borderColor: theme.colors.primary.lighter,
  },
}))

const MenuFooter = styled('div')(({ theme }) => ({
  padding: `8px 12px`,
  borderTop: `1px solid hsl(0, 0%, 90%)`,
  color: theme.colors.primary.main,
  fontWeight: 'bold',
  background: 'rgba(252, 76, 30, 0.08)',
  borderRadius: '0 0 8px 8px',
  fontSize: theme.fontSize.regular,
  textAlign: 'left',
}))

const CancelButton = styled(ResetButton)(({ theme }) => ({
  display: 'flex',
  alignSelf: 'stretch',
  alignItems: 'center',
  padding: `0 ${theme.spacing.sm}px`,
  cursor: 'pointer',
  color: theme.colors.primary.main,
  '&:hover': {
    color: theme.colors.primary.darken,
  },
}))

export const PreviewAutocomplete: FC<PreviewAutocompleteProps> = ({
  autoFocus,
  defaultTokens,
  onCancel,
  onSelect,
  originalRequest,
  searchRequest,
}) => {
  const theme = useAppTheme()
  const { t } = useTranslation()
  const [selectedOptions, setSelectedOptions] = useState<ChipOption[]>(
    () => defaultTokens?.map((token) => ({ label: token, value: token })) ?? [],
  )
  const [searchKey, setSearchKey] = useState(0)
  const [inputValue, setInputValue] = useState('')
  const [hasFocused, setHasFocused] = useState(!!autoFocus)

  const { run: loadOptions } = useDebounceFn(
    (inputValue: string, callback: (options: DropdownOption[]) => void) => {
      const tokens = selectedOptions.map((chip) => chip.value) as ApiCatalogToken[]
      const pattern = inputValue || (tokens.length === 0 ? searchRequest ?? originalRequest ?? '' : '')

      if (!pattern && tokens.length === 0) {
        return callback([])
      }

      apiClient.catalog.search({ pattern, tokens }).then((results) =>
        callback(
          results.map((item) => ({
            label: item.description,
            value: item.sku,
            tokens: item.tokens,
            unitsOfMeasure: item.unitsOfMeasure,
          })),
        ),
      )
    },
    { wait: 300 },
  )

  const handleChange = (newValue: readonly ChipOption[], actionMeta: ActionMeta<ChipOption>) => {
    if (actionMeta.action === 'select-option' && actionMeta.option) {
      const selected = actionMeta.option as DropdownOption
      setSelectedOptions(selected.tokens.map((token) => ({ label: token, value: token })))
      setInputValue('')
      onSelect?.({
        sku: selected.value,
        description: selected.label,
        tokens: selected.tokens,
        unitsOfMeasure: selected.unitsOfMeasure,
      })
    } else {
      if (actionMeta.action === 'remove-value' && actionMeta.removedValue) {
        const removedIndex = selectedOptions.findIndex((chip) => chip.value === actionMeta.removedValue.value)
        setSelectedOptions(selectedOptions.slice(0, removedIndex))
      } else {
        setSelectedOptions([...newValue])
      }
      setSearchKey((k) => k + 1)
    }
  }

  const MultiValueRemove = (props: MultiValueRemoveProps<ChipOption>) => (
    <components.MultiValueRemove {...props}>
      <XIcon size={14} />
    </components.MultiValueRemove>
  )

  const Menu = (props: MenuProps<ChipOption, true>) => (
    <components.Menu {...props}>
      {props.children}
      {originalRequest && <MenuFooter>{originalRequest}</MenuFooter>}
    </components.Menu>
  )

  return (
    <Container>
      <AsyncSelect<ChipOption, true>
        key={searchKey}
        isMulti
        autoFocus={autoFocus || searchKey > 0}
        value={selectedOptions}
        onChange={handleChange}
        components={{ MultiValueRemove, Menu, DropdownIndicator: null }}
        defaultOptions={hasFocused}
        openMenuOnFocus
        onFocus={() => {
          if (!hasFocused) {
            setHasFocused(true)
            setSearchKey((k) => k + 1)
          }
        }}
        inputValue={inputValue}
        onInputChange={(value, action) => {
          if (action.action !== 'input-blur' && action.action !== 'menu-close') {
            setInputValue(value)
          }
        }}
        loadOptions={loadOptions}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && onCancel) {
            e.preventDefault()
            onCancel()
          }
        }}
        placeholder={t('Type to search...')}
        noOptionsMessage={({ inputValue }) =>
          !inputValue && selectedOptions.length === 0 ? t('Type to search...') : t('No items found')
        }
        loadingMessage={() => t('Searching quote items...')}
        menuPortalTarget={document.body}
        styles={{
          container: (base) => ({
            ...base,
            flex: 1,
          }),
          control: (base) => ({
            ...base,
            minHeight: 'unset',
            border: 'none',
            boxShadow: 'none',
            backgroundColor: 'transparent',
          }),
          indicatorSeparator: () => ({ display: 'none' }),
          clearIndicator: () => ({ display: 'none' }),
          valueContainer: (base) => ({
            ...base,
            gap: theme.spacing.xs,
            padding: theme.spacing.xs,
          }),
          input: (base) => ({ ...base, margin: 0, padding: '2px 0' }),
          multiValue: (base) => ({
            ...base,
            backgroundColor: theme.colors.primary.main,
            margin: 0,
            borderRadius: 0,
          }),
          multiValueLabel: (base) => ({ ...base, color: theme.colors.primary.contrastText }),
          multiValueRemove: (base) => ({
            ...base,
            borderRadius: 0,
            color: theme.colors.primary.contrastText,
            '&:hover': { backgroundColor: theme.colors.primary.darken, color: theme.colors.primary.contrastText },
          }),
          placeholder: (base) => ({ ...base, color: theme.colors.text.primary, opacity: 0.5 }),
          menu: (base) => ({ ...base, borderRadius: 0 }),
          menuPortal: (base) => ({
            ...base,
            zIndex: 9999,
          }),
          loadingMessage: (base) => ({
            ...base,
            textAlign: 'left',
          }),
          noOptionsMessage: (base) => ({
            ...base,
            textAlign: 'left',
          }),
        }}
      />

      {onCancel && (
        <CancelButton onClick={onCancel}>
          <XIcon size={18} />
        </CancelButton>
      )}
    </Container>
  )
}
